import { PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { firstQueryError, isRetryingQueries, retryFailedQueries } from '@client/trpc/query-retry';
import type { ShareableRouteContext } from '@client/types/router';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { encodeBase64Json } from '@shared/utils/core/base64-json';
import { createFileRoute } from '@tanstack/react-router';
import { Tag } from 'antd';
import { ExternalLink } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type PdsVersionAction, PdsVersionByPdsEdit } from './PdsVersionByPdsEdit';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/$productTypeId/pds/$pdsId',
)({
    staticData: {
        ui: {
            title: 'PDS version configuration',
            subtitle: 'Manage versions for this Product Disclosure Statement',
        },
        presentation: 'fullPage',
    },
    params: {
        parse({ pdsId }) {
            return { pdsId: BigInt(pdsId) };
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        const cachedPds = trpcUtils.pds.getPds.getData({ id: params.pdsId });

        return {
            breadcrumb: {
                title: cachedPds?.name ?? 'PDS versions',
            },
        };
    },
    component: ConfigurationPdsVersionsByPds,
});

type QueryParams = RouterInputs['pdsVersion']['listPdsVersion'];
type PdsVersionRow = RouterOutputs['pdsVersion']['listPdsVersion']['rows'][number];
type PdsVersionOrderBy = NonNullable<QueryParams['orderBy']>;

function ConfigurationPdsVersionsByPds() {
    const orgId = useOrgId();
    const { pdsId, productTypeId } = Route.useParams();
    const navigate = Route.useNavigate();
    const { formatDate } = useFormatter();
    const [action, setAction] = useState<PdsVersionAction>(['none']);
    const hasHandledPdsRouteError = useRef(false);

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { id: 'desc' },
        where: {},
    });

    const {
        data: pds,
        error: pdsError,
        refetch: refetchPds,
        isFetching: pdsIsFetching,
    } = trpc.pds.getPds.useQuery(
        { id: pdsId },
        {
            enabled: !!pdsId,
            retry: false,
        },
    );

    const {
        data,
        isPending,
        error: pdsVersionError,
        refetch,
        isFetching,
    } = trpc.pdsVersion.listPdsVersion.useQuery(
        { ...queryParams, where: { ...queryParams.where, pdsId } },
        { enabled: !!pdsId && !!pds },
    );

    useEffect(() => {
        const errorCode = pdsError?.data?.code;
        if (hasHandledPdsRouteError.current || !errorCode) {
            return;
        }

        if (errorCode !== 'NOT_FOUND' && errorCode !== 'FORBIDDEN') {
            return;
        }

        hasHandledPdsRouteError.current = true;

        message.error(
            errorCode === 'NOT_FOUND'
                ? 'This PDS is no longer available. Redirecting to the product type PDS list.'
                : 'You do not have permission to view this PDS. Redirecting to the product type PDS list.',
        );

        void navigate({
            to: '/console/$orgId/configuration/claims-products/product-types/$productTypeId',
            params: { orgId, productTypeId },
            replace: true,
        });
    }, [navigate, orgId, pdsError?.data?.code, productTypeId]);

    if (pdsError?.data?.code === 'NOT_FOUND' || pdsError?.data?.code === 'FORBIDDEN') {
        return null;
    }

    const columnHelper = createColumnHelper<PdsVersionRow, PdsVersionOrderBy>();

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ row }) => row.id.toString(),
            width: 80,
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => <span className="font-medium">{row.name}</span>,
            hideable: false,
        }),
        columnHelper.accessor('startDate', {
            id: 'startDate',
            header: 'Start date',
            sortable: 'startDate',
            cell: ({ row }) =>
                row.startDate ? formatDate(row.startDate) : <span className="text-subtle-foreground">-</span>,
            width: 120,
        }),
        columnHelper.accessor('endDate', {
            id: 'endDate',
            header: 'End date',
            sortable: 'endDate',
            cell: ({ row }) =>
                row.endDate ? formatDate(row.endDate) : <span className="text-subtle-foreground">-</span>,
            width: 120,
        }),
        columnHelper.display({
            id: 'questionnaire',
            header: 'Questionnaire',
            cell: ({ row }) => row.Questionnaire?.name ?? <span className="text-subtle-foreground">-</span>,
        }),
        columnHelper.accessor('isEnabled', {
            id: 'isEnabled',
            header: 'Enabled',
            cell: ({ row }) =>
                row.isEnabled ? (
                    <Tag variant="outlined" color="green">
                        Yes
                    </Tag>
                ) : (
                    <Tag variant="outlined" color="red">
                        No
                    </Tag>
                ),
            width: 100,
            align: 'center',
        }),
        columnHelper.actions({
            cell: ({ row }) =>
                row.questionnaireId && (
                    <Button.Link
                        to="/public/$orgId/claim/lodgement/$pdsVersionId/$questionnaireId/$productTypeId"
                        params={{ orgId, pdsVersionId: row.id, questionnaireId: row.questionnaireId, productTypeId }}
                        search={{ data: encodeBase64Json({ insurerName: pds?.Insurer?.name }) }}
                        target="_blank"
                        variant="tertiary"
                        onClick={(e) => e.stopPropagation()}
                        icon={<ExternalLink />}
                        iconPosition="end"
                    >
                        Public lodgement
                    </Button.Link>
                ),
            width: 150,
        }),
    ];

    const tableProps = usePrismaTable<PdsVersionRow, QueryParams['where'], PdsVersionOrderBy>({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        emptyState: { noRecordsTitle: 'No versions of this PDS yet' },
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'pds-version-configuration-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    const pageQueries = [
        { error: pdsError, isFetching: pdsIsFetching, refetch: refetchPds },
        { error: pdsVersionError, isFetching, refetch },
    ];
    const queryError = firstQueryError(pageQueries);

    return (
        <PageController route={Route}>
            {queryError ? (
                <InlineQueryError
                    error={queryError}
                    onRetry={() => retryFailedQueries(pageQueries)}
                    retrying={isRetryingQueries(pageQueries)}
                />
            ) : (
                <>
                    <PdsVersionByPdsEdit
                        pdsId={pdsId}
                        action={action}
                        onClose={() => {
                            setAction(['none']);
                            void refetch();
                        }}
                    />

                    <TableToolbar
                        actions={
                            <Button icon={<PlusOutlined />} onClick={() => setAction(['add'])} type="primary">
                                New PDS version
                            </Button>
                        }
                    />

                    <PrismaTable
                        {...tableProps}
                        onRow={({ id }) => ({
                            className: 'cursor-pointer',
                            onClick() {
                                setAction(['edit', id]);
                            },
                        })}
                    />
                </>
            )}
        </PageController>
    );
}
